import { ActionContext, Store } from 'vuex';

import Router from '@/main/router';

import {
  signin,
  signup,
  details,
  logout,
  validateToken,
  zipCodeNotFound,
  addCustomerAddress,
  getCustomerAddresses,
  sendAcceptTerms,
  updateCustomer,
  updateCustomerById,
  userPhoneCreate,
} from '@/api/resources/customer';

import { isValid } from '@/helpers/values';
import { featureFlags } from '@/featureFlags';

import TraceableError from '@/classes/TraceableError';

import ICheckoutState from '@/interfaces/CheckoutState';
import { ICustomer, ICustomerAuth } from '@/interfaces/Customer';
import { IZipCodeNotFound } from '@/interfaces/ZipCodeNotFound';
import { ICustomerState } from './state';

import Cookie from '@/helpers/cookie';
import {
  EAuthenticationType,
  EAuthenticationAction,
  EAuthenticationOrigin,
  EAuthenticationTokenStatus,
} from '@/helpers/enums';
import { pushAuthenticationActionToDatalayer, pushRegisterBlockToDatalayer, pushCurrentActaUserIdToDatalayer } from '@/helpers/dataLayer';
import { EErrorExhibition, EErrorType, EErrorSeverity, EErrorScope, ECustomerKind } from '@/helpers/enums';
import { addError, getErrorByCodeAndType } from '@/helpers/errors';
import { getUserGoalPointSummaries } from '@/api/resources/rewards';
import { EInteractEventTrackerAction } from '../../InteractEventTracker/enums/EInteractEventTracker';
import { sendInteractEvent } from '@/helpers/events';

const hasGoBackRoute = (): boolean => {
  return isValid(Router.currentRoute.query.goBackRoute);
};

const isGoingToDashboard = (): boolean => {
  return Router.currentRoute.meta.navigationGuardType === 'authenticationDashboard';
};

const willRedirect = (): boolean => {
  if (hasGoBackRoute() || isGoingToDashboard()) {
    let redirectUrl: string = Router.currentRoute.query.goBackRoute as string;

    if (isGoingToDashboard() && !hasGoBackRoute()) {
      redirectUrl = process.env.VUE_APP_DASHBOARD_URL as string;
    }

    window.location.replace(redirectUrl);

    return true;
  }

  return false;
};

export default {
  signin({ commit, dispatch }: ActionContext<ICustomerState, any>, { customer, isEverywhere }: { customer: ICustomerAuth, isEverywhere?: boolean }): Promise<any> {
    return new Promise(async (resolve: any, reject: any) => {
      commit('requestSignin');

      sendInteractEvent({
        action: EInteractEventTrackerAction.CHK_LOGIN,
        options: {
          avoidDuplicate: true,
        },
      });

      try {
        const request = await signin(customer);
        const response = request.data.token;

        commit('successSignin', response);

        pushCurrentActaUserIdToDatalayer();

        pushAuthenticationActionToDatalayer({
          type: EAuthenticationType.manual,
          action: EAuthenticationAction.login,
          origin: customer.loginOrigin ? customer.loginOrigin : EAuthenticationOrigin.login,
          status: EAuthenticationTokenStatus.valid,
        });

        if (isEverywhere) {
          resolve(response);
        } else {
          if (!willRedirect()) {
            Router.push({
              name: 'payment',
              query: Router.currentRoute.query,
            });
          }

          commit('App/setPageLoading', true, { root: true });
          await dispatch('App/prepareCheckout', { slug: Router.currentRoute.params.slug }, { root: true });
          commit('App/setPageLoading', false, { root: true });

          sendInteractEvent({
            action: EInteractEventTrackerAction.CHK_LOGIN_SUCCESS,
            attributes: {
              return_code: request.status,
              return_message: request.statusText,
            },
          });

          resolve(response);
        }
      } catch (error) {
        commit('errorSignin');

        if (error.response) {
          addError(new TraceableError({
            code: error.response.status,
            type: EErrorType.authentication,
            scope: EErrorScope.login,
            trigger: 'signin',
            severity: error.response.status === 401 ? EErrorSeverity.warning : EErrorSeverity.critical,
            exhibition: EErrorExhibition.alert,
            message: error.response.data.message,
          }));

          sendInteractEvent({
            action: EInteractEventTrackerAction.CHK_LOGIN_FAILED,
            attributes: {
              return_code: error.response.status,
              return_message: error.response.data.message,
            },
          });
        }

        reject(error);
      }
    });
  },
  signup({ commit, dispatch }: ActionContext<{}, any>, { customer, isEverywhere }: { customer: ICustomer, isEverywhere: boolean }): Promise<any> {
    return new Promise(async (resolve: any, reject: any) => {
      commit('requestSignup');

      try {
        const request = await signup(customer);

        const idUser = new Cookie({ name: 'COOKIE_EMP_U' }).getSession().value;

        if (customer.terms_optin && idUser) {
          await sendAcceptTerms({
            idUser,
            optin: customer.terms_optin
          });

          const cookie = new Cookie({ name: 'COOKIE_EMP_ALLOW_COOKIES', value: 'allow' });
          cookie.setCookie();
        }

        sendInteractEvent({
          action: EInteractEventTrackerAction.CHK_REGISTER_SUCCESS,
          attributes: {
            return_code: request.status,
            return_message: request.statusText,
          },
        });

        commit('successSignup');

        await dispatch('signin', {
          customer: {
            username: customer.email,
            password: customer.password,
            loginOrigin: EAuthenticationOrigin.register,
          },
          isEverywhere,
        });

        pushAuthenticationActionToDatalayer({
          type: EAuthenticationType.manual,
          action: EAuthenticationAction.register,
          origin: EAuthenticationOrigin.register,
          status: EAuthenticationTokenStatus.valid,
        });

        resolve(customer);
      } catch (error) {
        commit('errorSignup');

        sendInteractEvent({
          action: EInteractEventTrackerAction.CHK_REGISTER_FAILED,
          attributes: {
            return_code: error.response.status,
            return_message: error.response.data.message,
          },
        });

        if (error.response) {
          const isInvalidNumber = error.response.data.cause
            && error.response.data.cause.includes('telefone');

          if (!isInvalidNumber) {
            addError(new TraceableError({
              code: error.response.status,
              type: EErrorType.authentication,
              scope: EErrorScope.register,
              trigger: 'signup',
              severity: error.response.status === 401 ? EErrorSeverity.warning : EErrorSeverity.critical,
              exhibition: EErrorExhibition.alert,
            }));
          }
        }

        reject(error);
      }
    });
  },
  details({ commit }: ActionContext<{}, any>): Promise<any> {
    return new Promise(async (resolve: any, reject: any) => {
      try {
        const request = await details();
        const customer: ICustomer = request.data;

        if (customer.kind === ECustomerKind.pj) {
          const errorCode = 1400;
          const error = getErrorByCodeAndType(errorCode, [EErrorType.eligibility]);

          addError(new TraceableError({
            ...error,
            type: EErrorType.authentication,
            scope: EErrorScope.global,
            trigger: 'details',
            severity: EErrorSeverity.critical,
            exhibition: EErrorExhibition.modal,
          }));
          return;
        }

        if (featureFlags.REGISTER_COMPLETION_ENABLED) {
          const addresses = customer.addresses || [];
          const hasAddresses = addresses.some(address => Boolean(address.is_active));
          const isIncomplete = !(hasAddresses && customer.name && customer.document);

          commit('setRegisterIsIncomplete', isIncomplete);

          isIncomplete && pushRegisterBlockToDatalayer({
            slug: Router.currentRoute.params.slug || Router.currentRoute.params.contract_id,
            acta_user_id: customer.acta_user_id
          });
        }

        commit('successDetails', customer);
        resolve(customer);
      } catch (error) {
        if (error.response) {
          addError(new TraceableError({
            code: error.response.status,
            type: EErrorType.authentication,
            scope: EErrorScope.global,
            trigger: 'details',
            severity: EErrorSeverity.critical,
            exhibition: EErrorExhibition.none,
          }));
        }

        reject(error);
      }
    });
  },
  logout({ state, rootState, commit, dispatch }: ActionContext<any, any>, isWidget?: boolean) {
    return new Promise(async (resolve: any, reject: any) => {
      commit('requestLogout');

      const token = state.token.getSession().value;

      await sendInteractEvent({ action: EInteractEventTrackerAction.CHK_LOGOUT });

      try {
        await validateToken(token);
        await logout();

        commit('successLogout');

        pushAuthenticationActionToDatalayer({
          type: EAuthenticationType.manual,
          action: EAuthenticationAction.logout,
          origin: EAuthenticationOrigin.logout,
          status: EAuthenticationTokenStatus.valid,
        });

        if (isWidget) {
          resolve();
        } else {
          const order: ICheckoutState['order'] = rootState.App.Checkout.order;
          const checkout: ICheckoutState['checkout'] = rootState.App.Checkout.checkout;

          const offer = Router.currentRoute.name !== 'renew'
            ? Router.currentRoute.params.slug || order && order.id_offer
            : checkout.cart.offer!.id;

          await dispatch('App/prepareCheckout', { slug: offer }, { root: true });

          if (!['errorPage', 'notFound'].includes(Router.currentRoute.name as string) && offer) {
            Router.push({
              name: 'checkout',
              params: {
                slug: offer
              },
              query: Router.currentRoute.query,
            });
          } else {
            Router.push({
              name: 'loginToDashboard',
              query: Router.currentRoute.query,
            });
          }

          commit('App/Checkout/setOrder', null, { root: true });

          resolve();
        }

      } catch (error) {
        commit('errorLogout');

        if (error.response) {
          addError(new TraceableError({
            code: error.response.status,
            type: EErrorType.authentication,
            scope: EErrorScope.global,
            trigger: 'logout',
            severity: EErrorSeverity.critical,
            exhibition: EErrorExhibition.none,
          }));
        }

        (error) && reject(error);
      }
    });
  },
  completeRegister({ commit }: ActionContext<ICustomerState, any>, customer: ICustomer): Promise<any> {
    return new Promise(async (resolve: any, reject: any) => {
      commit('setLoading', true);
      try {
        const { data } = await updateCustomer(customer);

        commit('successDetails', data);
        resolve(data);
      } catch (error) {
        reject(error);
      } finally {
        commit('setLoading', false);
      }
    });
  },
  notFoundZipCode(_: any, newCustomer: IZipCodeNotFound): Promise<any> {
    return zipCodeNotFound(newCustomer);
  },
  async saveDeliveryAddress({ state }: ActionContext<ICustomerState, any>) {
    const { acta_user_id, delivery_address } = state.customer;
    try {
      const { data } = await addCustomerAddress(acta_user_id as number, delivery_address);

      return data.acta_address_id || -1;
    } catch {
      return -1;
    }
  },
  async getAddresses({ commit, state }: ActionContext<ICustomerState, any>) {
    const { acta_user_id } = state.customer;

    if (!acta_user_id) return;

    try {
      const { data } = await getCustomerAddresses(acta_user_id as number);
      commit('setAddresses', data);
    } catch (error) {
      console.error(error);
    }
  },
  getRewardsSummary({ state, commit }: ActionContext<ICustomerState, any>): Promise<any> {
    return new Promise(async (resolve: any, reject: any) => {
      commit('requestRewardsSummary');

      try {
        const request = await getUserGoalPointSummaries(Number(state.customer.acta_user_id));
        const summary: any = request.data;

        commit('successRewardsSummary', summary);

        resolve(summary);
      } catch (error) {
        commit('errorRewardsSummary');

        if (error.response) {
          addError(new TraceableError({
            code: error.response.status,
            type: EErrorType.rewards,
            scope: EErrorScope.global,
            trigger: 'rewardsSummary',
            severity: EErrorSeverity.critical,
            exhibition: EErrorExhibition.none,
          }));
        }

        reject(error);
      }
    });
  },
  updateCustomer({ commit }: ActionContext<ICustomerState, any>, customer: ICustomer): Promise<any> {
    return new Promise(async (resolve: any, reject: any) => {
      try {
        await updateCustomerById(customer);
        resolve(true);
      } catch (error) {
        reject(error);
      }
    });
  },
  userPhoneCreate({ commit }: ActionContext<ICustomerState, any>, payload: any): Promise<any> {
    return new Promise(async (resolve: any, reject: any) => {
      try {
        await userPhoneCreate(payload);
        resolve(true);
      } catch (error) {
        reject(error);
      }
    });
  },
};
